import {
  styled, useTheme, SwipeableDrawer,
  Accordion, AccordionSummary, AccordionDetails, Paper, FormControlLabel, Stack,
  Box,
  BoxProps,
  Button,
  Grid,
  InputBase,
  Chip, useMediaQuery, Typography, Theme
} from "@mui/material";
//import { alpha } from "@mui/material/styles";
import { t } from "i18next";
import { ChevronDown, CircleX, LucideX, Settings2 } from "lucide-react";
import { ChangeEvent, ChangeEventHandler, SyntheticEvent, useEffect, useMemo, useState } from "react";
import { Checkbox, Icon } from "../../components";
import { ButtonProps } from "../../components/Button/Button.types";
import { ButtonDigited } from "../../components/Button/Button.variants";
import { useCampaignsQuery } from "../../queries/campaigns";
import {
  useAutotaggingCapabilitiesQuery,
  useAutotaggingKnowledgeQuery,
  useAutotaggingLevelsQuery
} from "../../queries/filters";
import { useAuthStore } from "../../stores/authStore";
/*
const FilterButton = styled(MuiButton)<ButtonProps>(({ theme, variant, color }) => ({
  alignItems: "center",
  borderRadius: theme.spacing(1),
  color:!color? (variant === "contained") ? theme.customColors.textPrimaryCta: theme.customColors.backgroundPrimaryCta:theme.palette.secondary?.main,
  display: "flex",
  fontSize: "1rem",
  fontWeight: 500,
  lineHeight: 1.5,
  // minWidth: "128px",
  padding: theme.spacing(1, 1),
  textTransform: "inherit",
  background: (variant === "contained") ? theme.customColors.backgroundPrimaryCta : "transparent",
  "&:hover": {
    backgroundColor: variant === "text" || variant == "outlined" ? "transparent" : darken(theme.customColors.backgroundPrimaryCta, 0.4)
  },
  "&:focus-visible": {
    border: "1px solid white !important"
  },
  "&.MuiButton-outlinedPrimary": {
    borderColor: theme.customColors.backgroundPrimaryCta,
    "&:hover": {
      backgroundColor: theme.customColors.backgroundPrimaryCta,
      "path":{
        "fill": theme.customColors.textPrimaryCta
      }
    }
  },
  "&.MuiButton-contained": {
    "path":{
      "fill": theme.customColors.textPrimaryCta
    }
  },

  "&.Mui-disabled": {
    background:theme.customColors.backgroundDisabled,
    color:theme.customColors.textDisabledAlternative,
    cursor:"not-allowed",
    pointerEvents:"auto"
  }
}));*/

const Search = styled("div")(({ theme }) => ({
  backgroundColor: theme.customColors.backgroundSecondary,
  borderRadius: theme.spacing(1),
  height: "2.5rem",
  position: "relative",
  marginBottom: "1.25rem"
}));

const SearchInput = ({ value, onChange, placeholder }: {
  value: string,
  onChange: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>, placeholder: string
}) => {
  return <Search>
    <SearchIconWrapper>
      <SearchIcon icon={ "Icons_search" } size={ 24 } />
    </SearchIconWrapper>
    <SearchInputBase
      value={ value }
      onChange={ onChange }
      inputProps={ { "aria-label": "search" } }
      placeholder={ `${t(placeholder)}` }
    />
    <CancelIconWrapper
      aria-label={ t("empty_field") }
      aria-hidden={ !value ? "true" : undefined }
      onClick={ () => {
        onChange({
          target: {
            value: ""
          }
        } as ChangeEvent<HTMLInputElement>);
      } }>
      {
        value ? (
          <CancelIcon
            aria-hidden="true"
            icon={ "close" }
            size={ "24px" }
          />
        ) : null
      }
    </CancelIconWrapper></Search>;
};

export const SelectChip = styled(Chip)(({ theme }) => ({
  backgroundColor: theme.customColors.backgroundPrimary,
  borderColor: theme.customColors.textPrimary,
  borderWidth: "1px",
  borderStyle: "solid",
  borderRadius: "5px",
  width: "auto",
  height: "1.5rem",
  cursor: "pointer",
  fontSize: "0.6rem",
  fontWeight: 700,
  "&:hover": {
    backgroundColor: theme.customColors.backgroundSecondaryCtaHover,
    border: `1px solid ${theme.customColors.borderPrimaryCta}`
  }
}));

const SelectChipLabel = styled("div")(({ theme }) => ({
  display: "flex",
  gap: "0.5rem",
  alignItems: "center",
  textTransform: "uppercase",
  color: theme.customColors.textPrimary

}));

const SelectedFilters = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
  marginLeft: "1rem",
  gap: "1rem"
  // [theme.breakpoints.down("sm")]: {
  //   // display: "none"
  // }
}));

const FiltersBar = styled("div")<{ padding?: string, paddingAdded?: boolean }>(({ paddingAdded }) => ({
  width: "100%",
  paddingTop: paddingAdded ? "2rem" : "1.5rem",
  paddingBottom: paddingAdded ? 0 : "1.5rem",
  display: "flex",
  justifyContent: "space-between"
}));

const FiltersButtonBar = styled("div")(() => ({
  alignSelf: "end",
  display: "flex",
  gap: "20px",
  marginTop: "auto"
}));

type AllFilters = {
  typeFilter: { SYNC?: boolean, ASYNC?: boolean, BLENDED?: boolean } | undefined,
  statusFilter: {
    E?: boolean,
    P?: boolean,
    C?: boolean,
    W?: boolean,
    T?: boolean,
    PA?: boolean,
    PW?: boolean,
    R?: boolean,
    U?: boolean,
    F?: boolean,
    PA0?: boolean,
    N?: boolean
  } | undefined,
  capabilitiesFilter: { [key: number]: boolean } | undefined,
  knowledgeAreaFilter: { [key: number]: boolean } | undefined,
  knowledgeDomainFilter: { [key: number]: boolean } | undefined,
  campaignsFilter: { [key: number]: boolean } | undefined,
  levelsFilter: { [key: number]: boolean } | undefined
};

export function FilterDrawer({ onChange, paddingAdded, nameKeyFilterStorage }: {
  onChange?: (query: URLSearchParams) => void,
  paddingAdded?: boolean,
  nameKeyFilterStorage: string
}) {
  const [open, setOpen] = useState(false);
  const campaignsEnabled = useAuthStore(state => state.corporateInfo?.collect_campaigns);
  const theme = useTheme();
  const {
    data: svcCapabilities,
    isFetched: areCapabilitiesFetched,
    isError: capabilitiesError
  } = useAutotaggingCapabilitiesQuery({ enabled: true });
  const {
    data: svcKnowledge,
    isFetched: knowledgeFetched, isError: knowledgeError
  } = useAutotaggingKnowledgeQuery({ enabled: true });
  const {
    data: svcCampaigns,
    isFetched: campaignsFetched, isError: campaignsError
  } = useCampaignsQuery({ enabled: campaignsEnabled });
  const {
    data: svcLevels,
    isFetched: areLevelsFetched, isError: levelsError
  } = useAutotaggingLevelsQuery({ enabled: true });
  const [knowledgeAreas, setKnowledgeAreas] = useState(svcKnowledge);
  const [capabilities, setCapabilities] = useState(svcCapabilities);
  const capabilitiesProfessional = useMemo(() => capabilities?.filter((c) => c.type === "PROFESSIONAL"),
    [capabilities]);
  const capabilitiesManager = useMemo(() => capabilities?.filter((c) => c.type === "MANAGER"), [capabilities]);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [typeFilter, setTypeFilter] = useState<{
    SYNC?: boolean,
    ASYNC?: boolean, BLENDED?: boolean
  } | undefined>(undefined);
  const [statusFilter, setStatusFilter] = useState<{
    E?: boolean,
    P?: boolean,
    C?: boolean,
    W?: boolean,
    T?: boolean,
    PA?: boolean,
    PW?: boolean,
    R?: boolean,
    U?: boolean,
    F?: boolean,
    PA0?: boolean,
    N?: boolean
  } | undefined>(undefined);
  const [capabilitiesFilter, setCapabilitiesFilter] = useState<{ [key: number]: boolean } | undefined>(undefined);
  const [knowledgeAreaFilter, setKnowledgeAreaFilter] = useState<{ [key: number]: boolean } | undefined>(undefined);
  const [knowledgeDomainFilter, setKnowledgeDomainFilter] = useState<{ [key: number]: boolean } | undefined>(undefined);
  const [campaignsFilter, setCampaignsFilter] = useState<{ [key: number]: boolean } | undefined>(undefined);
  const [levelsFilter, setLevelsFilter] = useState<{ [key: number]: boolean } | undefined>(undefined);
  const [capabilitiesSearch, setCapabilitiesSearch] = useState("");
  const [knowledgeSearch, setKnowledgeSearch] = useState("");
  const [filtersLoaded, setFiltersLoaded] = useState(false);
  const [filterSelected, setFilterSelected] = useState<number>(0);

  useEffect(() => {
    if (svcKnowledge && knowledgeFetched && !knowledgeError) {
      setKnowledgeAreas(svcKnowledge);
    }
  }, [svcKnowledge, knowledgeFetched, knowledgeError]);

  useEffect(() => {
    if (svcCapabilities && areCapabilitiesFetched && !capabilitiesError) {
      setCapabilities(svcCapabilities);
    }
  }, [svcCapabilities, areCapabilitiesFetched, capabilitiesError]);

  useEffect(() => {
    const filtersFromStorage = sessionStorage.getItem(nameKeyFilterStorage);
    let allFilters: AllFilters = {
      typeFilter: undefined,
      statusFilter: undefined,
      capabilitiesFilter: undefined,
      knowledgeAreaFilter: undefined,
      knowledgeDomainFilter: undefined,
      campaignsFilter: undefined,
      levelsFilter: undefined
    };
    if (filtersFromStorage != null) {
      try {
        allFilters = JSON.parse(filtersFromStorage);
      } finally {
        sessionStorage.removeItem(nameKeyFilterStorage);
      }
    }

    if (allFilters.typeFilter) {
      setTypeFilter(allFilters.typeFilter);
    } else {
      setTypeFilter(undefined);
    }
    if (allFilters.campaignsFilter) {
      setCampaignsFilter(allFilters.campaignsFilter);
    } else {
      setCampaignsFilter(undefined);
    }
    if (allFilters.capabilitiesFilter) {
      setCapabilitiesFilter(allFilters.capabilitiesFilter);
    } else {
      setCapabilitiesFilter(undefined);
    }
    if (allFilters.knowledgeAreaFilter) {
      setKnowledgeAreaFilter(allFilters.knowledgeAreaFilter);
    } else {
      setKnowledgeAreaFilter(undefined);
    }
    if (allFilters.knowledgeDomainFilter) {
      setKnowledgeDomainFilter(allFilters.knowledgeDomainFilter);
    } else {
      setKnowledgeDomainFilter(undefined);
    }
    if (allFilters.levelsFilter) {
      setLevelsFilter(allFilters.levelsFilter);
    } else {
      setLevelsFilter(undefined);
    }
    if (allFilters.statusFilter) {
      setStatusFilter(allFilters.statusFilter);
    } else {
      setStatusFilter(undefined);
    }
    if (Object.values(allFilters).find(item => !!item)) {
      applyFiltersCallback(
        allFilters.typeFilter,
        allFilters.statusFilter,
        allFilters.capabilitiesFilter,
        allFilters.knowledgeAreaFilter,
        allFilters.knowledgeDomainFilter,
        allFilters.campaignsFilter,
        allFilters.levelsFilter
      );
    } else {
      applyFiltersCallback(
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined);
    }
    setFiltersLoaded(true);
  }, [filtersLoaded]);

  const applyFiltersCallback = (typeFilter: { SYNC?: boolean, ASYNC?: boolean, BLENDED?: boolean } | undefined,
    statusFilter: {
                                  E?: boolean,
                                  P?: boolean,
                                  C?: boolean,
                                  W?: boolean,
                                  T?: boolean,
                                  PA?: boolean,
                                  PW?: boolean,
                                  R?: boolean,
                                  U?: boolean,
                                  F?: boolean,
                                  PA0?: boolean,
                                  N?: boolean
                                } | undefined,
    capabilitiesFilter: { [key: number]: boolean } | undefined,
    knowledgeAreaFilter: { [key: number]: boolean } | undefined,
    knowledgeDomainFilter: { [key: number]: boolean } | undefined,
    campaignsFilter: { [key: number]: boolean } | undefined,
    levelsFilter: { [key: number]: boolean } | undefined) => {
    const allFilters: AllFilters = {
      typeFilter,
      statusFilter,
      capabilitiesFilter,
      knowledgeAreaFilter,
      knowledgeDomainFilter,
      campaignsFilter,
      levelsFilter
    };
    sessionStorage.setItem(nameKeyFilterStorage, JSON.stringify(allFilters));
    let query: string[][] = [];

    if (typeFilter) {
      query.push(...Object.entries(typeFilter)
        .filter((entry) => entry[1]).map(entry => ["learning_object_type", entry[0]]));
    }
    if (statusFilter) {
      query.push(...Object.entries(statusFilter)
        .filter(entry => entry[1]).map(entry => ["enroll_status", entry[0]]));
    }
    if (capabilitiesFilter) {
      query.push(...Object.entries(capabilitiesFilter)
        .filter(entry => entry[1]).map(entry => ["autotagging_capabilities", entry[0]]));
    }
    if (knowledgeAreaFilter) {
      query.push(...Object.entries(knowledgeAreaFilter)
        .filter(entry => entry[1]).map(entry => ["autotagging_knowledge_areas", entry[0]]));
    }
    if (knowledgeDomainFilter) {
      query.push(...Object.entries(knowledgeDomainFilter)
        .filter(entry => entry[1]).map(entry => ["autotagging_knowledge_domains", entry[0]]));
    }
    if (campaignsFilter) {
      query.push(...Object.entries(campaignsFilter)
        .filter(entry => entry[1]).map(entry => ["campaigns", entry[0]]));
    }
    if (levelsFilter) {
      query.push(...Object.entries(levelsFilter)
        .filter(entry => entry[1]).map(entry => ["autotagging_levels", entry[0]]));
    }
    let filterSelectedCount = 0;
    if (query.length > 0) {
      filterSelectedCount = query.length;
    }
    setFilterSelected(filterSelectedCount);
    onChange?.(new URLSearchParams(query));
    setOpen(false);
  };
  const typeChips = Object.entries(typeFilter ?? {}).filter(entry => entry[1]).map((entry) => {
    let label = "";
    if (entry[0] === "SYNC") {
      label = "filters_live";
    }
    if (entry[0] === "ASYNC") {
      label = "filters_digital";
    }
    if (entry[0] === "BLENDED") {
      label = "filters_blended";
    }
    return <SelectChip id={ `selectChipType-${entry[0]}` } key={ `type${entry[0]}` }
      variant="outlined"
      onClick={ () => {
        const newFilter = { ...typeFilter, [entry[0]]: false };
        if (Object.values(newFilter).every(v => !v)) {
          setTypeFilter(undefined);
          applyFiltersCallback(undefined,
            statusFilter, capabilitiesFilter, knowledgeAreaFilter,
            knowledgeDomainFilter, campaignsFilter, levelsFilter);
        } else {
          setTypeFilter(newFilter);
          applyFiltersCallback(newFilter,
            statusFilter, capabilitiesFilter, knowledgeAreaFilter,
            knowledgeDomainFilter, campaignsFilter, levelsFilter);
        }
      } }
      label={ <SelectChipLabel>{ t(label) }
        <CircleX size={ 12 } color={ theme.customColors.systemSecondary01 } />
      </SelectChipLabel> }
    />;
  });
  const statusChips = Object.entries(statusFilter ?? {}).filter((entry) => entry[1]).map((entry) => {
    let label = "";
    if (entry[0] === "E") {
      label = "filters_enrolled";
    }
    if (entry[0] === "P") {
      label = "filters_pending";
    }
    if (entry[0] === "C") {
      label = "filters_completed";
    }
    if (["W", "T", "PA", "PW", "R", "U", "F", "PA0", "N"].includes(entry[0])) {
      return undefined;
    }
    return <SelectChip key={ `status${entry[0]}` }
      variant="outlined"
      onClick={ () => {
        const newFilter = entry[0] !== "E" ? { ...statusFilter, [entry[0]]: false } : {
          ...statusFilter,
          "E": false, "F": false, "N": false, "PA": false, "PA0": false, "PW": false,
          "R": false, "T": false, "U": false, "W": false
        };
        if (Object.values(newFilter).every(v => !v)) {
          setStatusFilter(undefined);
          applyFiltersCallback(typeFilter,
            undefined, capabilitiesFilter, knowledgeAreaFilter,
            knowledgeDomainFilter, campaignsFilter, levelsFilter);
        } else {
          setStatusFilter(newFilter);
          applyFiltersCallback(typeFilter, newFilter, capabilitiesFilter,
            knowledgeAreaFilter, knowledgeDomainFilter, campaignsFilter, levelsFilter);
        }
      } }
      label={ <SelectChipLabel>{ t(label) }
        <CircleX size={ 12 } color={ theme.customColors.systemSecondary01 } />
      </SelectChipLabel> }
    />;
  });
  const kaChips = Object.entries(knowledgeAreaFilter ?? {}).filter((entry) => entry[1]).map((entry) => {
    const ka = svcKnowledge?.find((k) => `${k.id}` === entry[0]);
    return <SelectChip key={ `ka${entry[0]}` }
      variant="outlined"
      onClick={ () => {
        const newFilter = { ...knowledgeAreaFilter, [entry[0]]: false };
        if (Object.values(newFilter).every(v => !v)) {
          setKnowledgeAreaFilter(undefined);
          applyFiltersCallback(typeFilter, statusFilter, capabilitiesFilter,
            undefined, knowledgeDomainFilter, campaignsFilter, levelsFilter);
        } else {
          setKnowledgeAreaFilter(newFilter);
          applyFiltersCallback(typeFilter, statusFilter, capabilitiesFilter,
            newFilter, knowledgeDomainFilter, campaignsFilter, levelsFilter);
        }
      } }
      label={ <SelectChipLabel>{ ka?.name }
        <CircleX size={ 12 } color={ theme.customColors.systemSecondary01 } />
      </SelectChipLabel> }
    />;
  });
  const kdChips = Object.entries(knowledgeDomainFilter ?? {}).filter(entry => entry[1]).map((entry) => {
    const kd = svcKnowledge?.map(k => k.children).flat().find((k) => `${k.id}` === entry[0]);
    return <SelectChip key={ `kd${entry[0]}` }
      variant="outlined"
      onClick={ () => {
        const newFilter = { ...knowledgeDomainFilter, [entry[0]]: false };
        if (Object.values(newFilter).every(v => !v)) {
          setKnowledgeDomainFilter(undefined);
          applyFiltersCallback(typeFilter, statusFilter, capabilitiesFilter,
            knowledgeAreaFilter, undefined, campaignsFilter, levelsFilter);
        } else {
          setKnowledgeDomainFilter(newFilter);
          applyFiltersCallback(typeFilter, statusFilter, capabilitiesFilter,
            knowledgeAreaFilter, newFilter, campaignsFilter, levelsFilter);
        }
      } }
      label={ <SelectChipLabel>{ kd?.name }
        <CircleX size={ 12 } color={ theme.customColors.systemSecondary01 } />
      </SelectChipLabel> }
    />;
  });
  const capabilityChips = Object.entries(capabilitiesFilter ?? {}).filter(entry => entry[1]).map((entry) => {
    const c = svcCapabilities?.find((c) => `${c.id}` === entry[0]);
    return <SelectChip key={ `capability${entry[0]}` }
      variant="outlined"
      onClick={ () => {
        const newFilter = { ...capabilitiesFilter, [entry[0]]: false };
        if (Object.values(newFilter).every(v => !v)) {
          setCapabilitiesFilter(undefined);
          applyFiltersCallback(typeFilter, statusFilter, undefined,
            knowledgeAreaFilter, knowledgeDomainFilter, campaignsFilter, levelsFilter);
        } else {
          setCapabilitiesFilter(newFilter);
          applyFiltersCallback(typeFilter, statusFilter, newFilter,
            knowledgeAreaFilter, knowledgeDomainFilter, campaignsFilter, levelsFilter);
        }
      } }
      label={ <SelectChipLabel>{ c?.name }
        <CircleX size={ 12 } color={ theme.customColors.systemSecondary01 } />
      </SelectChipLabel> }
    />;
  });
  const levelsChips = Object.entries(levelsFilter ?? {}).filter(entry => entry[1]).map((entry) => {
    const l = svcLevels?.find((c) => `${c.id}` === entry[0]);
    return <SelectChip key={ `level${entry[0]}` }
      variant="outlined"
      onClick={ () => {
        const newFilter = { ...levelsFilter, [entry[0]]: false };
        if (Object.values(newFilter).every(v => !v)) {
          setLevelsFilter(undefined);
          applyFiltersCallback(typeFilter, statusFilter, capabilitiesFilter,
            knowledgeAreaFilter, knowledgeDomainFilter, campaignsFilter, undefined);
        } else {
          setLevelsFilter(newFilter);
          applyFiltersCallback(typeFilter, statusFilter, capabilitiesFilter,
            knowledgeAreaFilter, knowledgeDomainFilter, campaignsFilter, newFilter);
        }
      } }
      label={ <SelectChipLabel>{ l?.name }
        <CircleX size={ 12 } color={ theme.customColors.systemSecondary01 } />
      </SelectChipLabel> }
    />;
  });
  const campaignsChips = Object.entries(campaignsFilter ?? {}).filter(entry => entry[1]).map((entry) => {
    const c = svcCampaigns?.output?.find((c) => `${c.id}` === entry[0]);
    return <SelectChip key={ `campaign${entry[0]}` }
      variant="outlined"
      onClick={ () => {
        const newFilter = { ...campaignsFilter, [entry[0]]: false };
        if (Object.values(newFilter).every(v => !v)) {
          setCampaignsFilter(undefined);
          applyFiltersCallback(typeFilter, statusFilter, capabilitiesFilter,
            knowledgeAreaFilter, knowledgeDomainFilter, undefined, levelsFilter);
        } else {
          setCampaignsFilter(newFilter);
          applyFiltersCallback(typeFilter, statusFilter, capabilitiesFilter,
            knowledgeAreaFilter, knowledgeDomainFilter, newFilter, levelsFilter);
        }
      } }
      label={ <SelectChipLabel>{ c?.name }
        <CircleX size={ 12 } color={ theme.customColors.systemSecondary01 } />
      </SelectChipLabel> }
    />;
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleClose: any = (event: SyntheticEvent, reason: string) => {
    if (reason === "backdropClick") {
      setFiltersLoaded(false);
    }
    setOpen(false);
  };

  return <>
    <FiltersBar paddingAdded={ paddingAdded }>
      <ButtonDigited
        typology="secondary"
        size="small"
        onClick={ () => setOpen(!open) }
      >
        <div style={ { padding: "0rem 0.5rem", display: "flex", alignItems: "center", gap: "0.5rem" } }>
          <Settings2 size={ 16 } />
          { t("filters") }
        </div>
      </ButtonDigited>
      <Box
        sx={ {
          flex: 1,
          height: "1px",
          backgroundColor: theme.palette.divider,
          opacity: 0.5,
          alignSelf: "center",
          marginLeft: "20px"
        } }
      />
      <SelectedFilters id="selected-filters-bar">
        { !isMobile ? (
          <>
            { typeChips }
            { statusChips }
            { kaChips }
            { kdChips }
            { capabilityChips }
            { levelsChips }
            { campaignsChips }

          </>
        ) : (
          <Typography fontSize="0.875rem" fontWeight="500">
            { filterSelected > 0 ? (
              t("filters_active", "Filtri attivi")
            ) : "" }
          </Typography>
        ) }
      </SelectedFilters>
    </FiltersBar>
    <SwipeableDrawer
      open={ open }
      anchor={ "left" }
      onClose={ handleClose }
      onOpen={ () => {
        setOpen(true);
      } }
      PaperProps={ {
        sx: {
          background: theme.customColors.backgroundPrimary,
          boxShadow: "none",
          // height: "calc(100% - 6rem)",
          overflowY: "visible",
          // top:  isMobile ? "8.8rem" : " 5rem",
          [theme.breakpoints.down("sm")]: {
            maxWidth: "100%"
            // right: "20px"
          },

          ".MuiPaper-root": {
            overflowX: "hidden",
            overflowY: "auto",

            "&:hover": {
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: theme.customColors.backgroundSecondary
              }
            },

            "::-webkit-scrollbar": {
              width: "6px"

            },

            "::-webkit-scrollbar-thumb": {
              backgroundColor: "transparent",
              borderRadius: "8px"
            }
          }

        }
      } }
      slotProps={ { backdrop: { classes: { root: "backdrop-widget" } } } }
      sx={ { position: "relative", zIndex: "1400" } }
    >
      <ContentContainer>
        {
          // force the widgets to re-render so they
          // trigger "fetching on mount" for the queries
          open ? (
            <WidgetContainer id="filterContainer" sx={ {
              ".accordionDetail":{
                color: theme.customColors.exploreCardText
              },
              ".accordionSummary":{
                color: theme.customColors.exploreCardText
              },
              ".chevron":{
                stroke: theme.customColors.exploreCardText
              }
            } }>
              <CloseDrawerButton
                className={ open ? "visible" : "" }
                theme={ theme }
                onClick={ () => {
                  setOpen(false);
                  setFiltersLoaded(false);
                } }
              />
              <FiltersAccordion>
                <AccordionSummary
                  className="accordionSummary"
                  sx={ {
                    fontSize: "16px",
                    lineHeight: "21px",
                    fontWeight: "bold"
                  } } 
                  expandIcon={
                    <ChevronDown className="chevron" size={ 16 } /> 
                  }
                >
                  <span>{ t("filters_type") }</span>
                </AccordionSummary>
                <AccordionDetails className="accordionDetail">
                  <Stack 
                    sx={ { 
                      width: "100%" 
                    } }
                  >
                    <FormControlLabel
                      sx={ {
                        marginLeft: "-9px", marginRight: "0",
                        ".MuiFormControlLabel-label": {
                          fontSize: "0.875rem"
                        }
                      } }
                      control={ <FiltersCheckbox checked={ !typeFilter } onChange={ (evt) => {
                        if (evt.target.checked) {
                          setTypeFilter(undefined);
                        }
                      } }></FiltersCheckbox> } label={ t("filters_all") }></FormControlLabel>
                    <FormControlLabel
                      sx={ {
                        marginLeft: "-9px", marginRight: "0",
                        ".MuiFormControlLabel-label": {
                          fontSize: "0.875rem"
                        }
                      } }
                      control={ <FiltersCheckbox checked={ !!typeFilter?.ASYNC } onChange={ (evt) => {
                        setTypeFilter((old) => {
                          const newFilter = { ...old, ASYNC: evt.target.checked };
                          if (Object.values(newFilter).every(v => !v)) {
                            return undefined;
                          }
                          return newFilter;
                        });
                      } }></FiltersCheckbox> } label={ t("filters_digital") }></FormControlLabel>
                    <FormControlLabel
                      sx={ {
                        marginLeft: "-9px", marginRight: "0",
                        ".MuiFormControlLabel-label": {
                          fontSize: "0.875rem"
                        }
                      } }
                      control={ <FiltersCheckbox checked={ !!typeFilter?.BLENDED } onChange={ (evt) => {
                        setTypeFilter((old) => {
                          const newFilter = { ...old, BLENDED: evt.target.checked };
                          if (Object.values(newFilter).every(v => !v)) {
                            return undefined;
                          }
                          return newFilter;
                        });
                      } }></FiltersCheckbox> } label={ t("filters_blended") }></FormControlLabel>
                    <FormControlLabel
                      sx={ {
                        marginLeft: "-9px", marginRight: "0",
                        ".MuiFormControlLabel-label": {
                          fontSize: "0.875rem"
                        }
                      } }
                      control={ <FiltersCheckbox checked={ !!typeFilter?.SYNC } onChange={ (evt) => {
                        setTypeFilter((old) => {
                          const newFilter = { ...old, SYNC: evt.target.checked };
                          if (Object.values(newFilter).every(v => !v)) {
                            return undefined;
                          }
                          return newFilter;
                        });
                      } }></FiltersCheckbox> } label={ t("filters_live") }></FormControlLabel>
                  </Stack>
                </AccordionDetails>
              </FiltersAccordion>
              <FiltersAccordion>
                <AccordionSummary
                  className="accordionSummary"
                  sx={ {
                    fontSize: "16px",
                    lineHeight: "21px",
                    fontWeight: "bold"
                  } } 
                  expandIcon={
                    <ChevronDown className="chevron" size={ 16 }/> }
                >
                  <span>{ t("filters_status") }</span>
                </AccordionSummary>
                <AccordionDetails className="accordionDetail">
                  <Stack sx={ { width: "100%" } }>
                    <FormControlLabel
                      sx={ {
                        marginLeft: "-9px", marginRight: "0",
                        ".MuiFormControlLabel-label": {
                          fontSize: "0.875rem"
                        }
                      } }
                      control={ <FiltersCheckbox checked={ !statusFilter } onChange={ (evt) => {
                        if (evt.target.checked) {
                          setStatusFilter(undefined);
                        }
                      } }></FiltersCheckbox> } label={ t("filters_all") }></FormControlLabel>
                    <FormControlLabel
                      sx={ {
                        marginLeft: "-9px", marginRight: "0",
                        ".MuiFormControlLabel-label": {
                          fontSize: "0.875rem"
                        }
                      } }
                      control={ <FiltersCheckbox checked={ !!statusFilter?.E } onChange={ (evt) => {
                        setStatusFilter((old) => {
                          const newFilter = {
                            ...old, E: evt.target.checked,
                            F: evt.target.checked,
                            N: evt.target.checked,
                            PA: evt.target.checked,
                            PA0: evt.target.checked,
                            PW: evt.target.checked,
                            R: evt.target.checked,
                            T: evt.target.checked,
                            U: evt.target.checked,
                            W: evt.target.checked
                          };
                          if (Object.values(newFilter).every(v => !v)) {
                            return undefined;
                          }
                          return newFilter;
                        });
                      } }></FiltersCheckbox> } label={ t("filters_enrolled") }></FormControlLabel>
                    <FormControlLabel
                      sx={ {
                        marginLeft: "-9px", marginRight: "0",
                        ".MuiFormControlLabel-label": {
                          fontSize: "0.875rem"
                        }
                      } }
                      control={ <FiltersCheckbox checked={ !!statusFilter?.P } onChange={ (evt) => {
                        setStatusFilter((old) => {
                          const newFilter = { ...old, P: evt.target.checked };
                          if (Object.values(newFilter).every(v => !v)) {
                            return undefined;
                          }
                          return newFilter;
                        });
                      } }></FiltersCheckbox> } label={ t("filters_pending") }></FormControlLabel>
                    <FormControlLabel
                      sx={ {
                        marginLeft: "-9px", marginRight: "0",
                        ".MuiFormControlLabel-label": {
                          fontSize: "0.875rem"
                        }
                      } }
                      control={ <FiltersCheckbox checked={ !!statusFilter?.C } onChange={ (evt) => {
                        setStatusFilter((old) => {
                          const newFilter = { ...old, C: evt.target.checked };
                          if (Object.values(newFilter).every(v => !v)) {
                            return undefined;
                          }
                          return newFilter;
                        });
                      } }></FiltersCheckbox> } label={ t("filters_completed") }></FormControlLabel>
                  </Stack>
                </AccordionDetails>
              </FiltersAccordion>
              { (knowledgeFetched && !knowledgeError && svcKnowledge?.length) ? <FiltersAccordion>
                <AccordionSummary 
                  className="accordionSummary"
                  sx={ {
                    fontSize: "16px",
                    lineHeight: "21px",
                    fontWeight: "bold"
                  } } 
                  expandIcon={
                    <ChevronDown className="chevron" size={ 16 } /> }
                >
                  <span>{ t("filters_knowledge") }</span>
                </AccordionSummary>
                <AccordionDetails className="accordionDetail">
                  <Stack sx={ { width: "100%" } }>
                    <SearchInput placeholder="filters_knowledge" value={ knowledgeSearch } onChange={ (evt) => {
                      setKnowledgeSearch(evt.target.value);
                      setKnowledgeAreas(svcKnowledge?.filter((ka) => ka.name.toLocaleLowerCase().includes(evt.target.value.toLocaleLowerCase()) || !!(ka.children.find(kd => kd.name.toLocaleLowerCase().includes(evt.target.value.toLocaleLowerCase())))));
                    } }></SearchInput>
                    <FormControlLabel
                      sx={ {
                        marginLeft: "-9px", marginRight: "0",
                        ".MuiFormControlLabel-label": {
                          fontSize: "0.875rem"
                        }
                      } }
                      control={ <FiltersCheckbox checked={ !knowledgeAreaFilter && !knowledgeDomainFilter }
                        onChange={ (evt) => {
                          if (evt.target.checked) {
                            setKnowledgeAreaFilter(undefined);
                            setKnowledgeDomainFilter(undefined);
                          }
                        } }></FiltersCheckbox> } label={ t("filters_all") }></FormControlLabel>
                    { knowledgeAreas?.map((ka) => {
                      return <KnowledgeAccordion key={ ka.id }>
                        <AccordionSummary
                          className="accordionSummary"
                          expandIcon={
                            <ChevronDown className="chevron" size={ 12 } />
                          }>
                          <FormControlLabel
                            sx={ {
                              marginLeft: "-9px", marginRight: "0",
                              ".MuiFormControlLabel-label": {
                                fontSize: "0.875rem"
                              }
                            } }
                            control={ <FiltersCheckbox checked={ !!(knowledgeAreaFilter?.[ka.id]) }
                              onClick={ (evt) => {
                                evt.stopPropagation();
                              } }
                              onChange={ (evt) => {
                                setKnowledgeAreaFilter((old) => {
                                  const newFilter = { ...old, [ka.id]: evt.target.checked };
                                  if (Object.values(newFilter).every(v => !v)) {
                                    return undefined;
                                  }
                                  return newFilter;
                                });
                              } }></FiltersCheckbox> } label={ ka.name }></FormControlLabel>
                        </AccordionSummary>
                        <AccordionDetails className="accordionDetail">
                          { ka.children
                            .filter((kd) => ka.name.toLocaleLowerCase().includes(knowledgeSearch.toLocaleLowerCase()) || kd.name.toLocaleLowerCase().includes(knowledgeSearch.toLocaleLowerCase()))
                            .map((kd) => {
                              return <FormControlLabel
                                sx={ {
                                  marginLeft: "-9px",
                                  marginRight: "0",
                                  ".MuiFormControlLabel-label": {
                                    fontSize: "0.75rem"
                                  }
                                } }
                                key={ kd.id }
                                control={ <FiltersCheckbox checked={ !!(knowledgeDomainFilter?.[kd.id]) }
                                  onChange={ (evt) => {
                                    setKnowledgeDomainFilter((old) => {
                                      const newFilter = { ...old, [kd.id]: evt.target.checked };
                                      if (Object.values(newFilter).every(v => !v)) {
                                        return undefined;
                                      }
                                      return newFilter;
                                    });
                                  } } sx={ {
                                    ".MuiSvgIcon-root": {
                                      height: "1.125rem", width: "1.125rem"
                                    }
                                  } } />
                                } label={ kd.name }
                              />;
                            }) }
                        </AccordionDetails>
                      </KnowledgeAccordion>;
                    }) }
                  </Stack>
                </AccordionDetails>
              </FiltersAccordion> : <></> }
              { (areCapabilitiesFetched && !capabilitiesError && svcCapabilities?.length) ? <FiltersAccordion>
                <AccordionSummary
                  className="accordionSummary"
                  sx={ {
                    fontSize: "16px",
                    lineHeight: "21px",
                    fontWeight: "bold"
                  } } expandIcon={
                    <ChevronDown className="chevron" size={ 16 } /> }>
                  <span>{ t("filters_capability") }</span>
                </AccordionSummary>
                <AccordionDetails className="accordionDetail">
                  <Stack sx={ { width: "100%" } }>
                    <SearchInput placeholder="filters_capability" value={ capabilitiesSearch } onChange={ (evt) => {
                      setCapabilitiesSearch(evt.target.value);
                      setCapabilities(svcCapabilities?.filter((capability) => capability.name.toLocaleLowerCase().includes(evt.target.value.toLocaleLowerCase())));
                    } }></SearchInput>
                    <FormControlLabel
                      sx={ {
                        marginLeft: "-9px", marginRight: "0",
                        ".MuiFormControlLabel-label": {
                          fontSize: "0.875rem"
                        }
                      } }
                      control={ <FiltersCheckbox checked={ !capabilitiesFilter } onChange={ (evt) => {
                        if (evt.target.checked) {
                          setCapabilitiesFilter(undefined);
                        }
                      } }></FiltersCheckbox> } label={ t("filters_all") }
                    />
                    { capabilitiesProfessional?.length ?
                      <KnowledgeAccordion id="PROFESSIONAL">
                        <AccordionSummary
                          className="accordionSummary"
                          expandIcon={
                            <ChevronDown className="chevron" size={ 12 } />
                          }>
                          <FormControlLabel
                            sx={ {
                              marginLeft: "-9px", marginRight: "0",
                              ".MuiFormControlLabel-label": {
                                fontSize: "0.875rem"
                              }
                            } }
                            control={ <FiltersCheckbox
                              checked={ capabilitiesProfessional.every((c) => capabilitiesFilter?.[c.id]) }
                              onClick={ (evt) => {
                                evt.stopPropagation();
                              } }
                              onChange={ (evt) => {
                                setCapabilitiesFilter((old) => {
                                  const newFilter = { ...old };
                                  for (const cap of capabilitiesProfessional) {
                                    newFilter[cap.id] = evt.target.checked;
                                  }
                                  if (Object.values(newFilter).every(v => !v)) {
                                    return undefined;
                                  }
                                  return newFilter;
                                });
                              } }></FiltersCheckbox> }
                            label={ t("filters_capability_type_professional") }></FormControlLabel>
                        </AccordionSummary>
                        <AccordionDetails className="accordionDetail">
                          { capabilitiesProfessional.map((capability) => {
                            return <FormControlLabel
                              sx={ {
                                marginLeft: "-9px", marginRight: "0",
                                ".MuiFormControlLabel-label": {
                                  fontSize: "0.875rem"
                                }
                              } }
                              key={ capability.id }
                              control={ <FiltersCheckbox
                                checked={ !!(capabilitiesFilter?.[capability.id]) } onChange={ (evt) => {
                                  setCapabilitiesFilter((old) => {
                                    const newFilter = { ...old, [capability.id]: evt.target.checked };
                                    if (Object.values(newFilter).every(v => !v)) {
                                      return undefined;
                                    }
                                    return newFilter;
                                  });
                                } }></FiltersCheckbox> } label={ capability.name }
                            />;
                          }) }
                        </AccordionDetails>
                      </KnowledgeAccordion> : <></> }
                    { capabilitiesManager?.length ? <KnowledgeAccordion id="MANAGER">
                      <AccordionSummary
                        className="accordionSummary"
                        expandIcon={
                          <ChevronDown className="chevron" size={ 12 } />
                        }>
                        <FormControlLabel
                          sx={ {
                            marginLeft: "-9px", marginRight: "0",
                            ".MuiFormControlLabel-label": {
                              fontSize: "0.875rem"
                            }
                          } }
                          control={ <FiltersCheckbox
                            checked={ capabilitiesManager.every((c) => capabilitiesFilter?.[c.id]) }
                            onClick={ (evt) => {
                              evt.stopPropagation();
                            } }
                            onChange={ (evt) => {
                              setCapabilitiesFilter((old) => {
                                const newFilter = { ...old };
                                for (const cap of capabilitiesManager) {
                                  newFilter[cap.id] = evt.target.checked;
                                }
                                if (Object.values(newFilter).every(v => !v)) {
                                  return undefined;
                                }
                                return newFilter;
                              });
                            } }></FiltersCheckbox> } label={ t("filters_capability_type_manager") }></FormControlLabel>
                      </AccordionSummary>
                      <AccordionDetails className="accordionDetail">
                        { capabilitiesManager.map((capability) => {
                          return <FormControlLabel
                            sx={ {
                              marginLeft: "-9px", marginRight: "0",
                              ".MuiFormControlLabel-label": {
                                fontSize: "0.875rem"
                              }
                            } }
                            key={ capability.id }
                            control={ <FiltersCheckbox
                              checked={ !!(capabilitiesFilter?.[capability.id]) } onChange={ (evt) => {
                                setCapabilitiesFilter((old) => {
                                  const newFilter = { ...old, [capability.id]: evt.target.checked };
                                  if (Object.values(newFilter).every(v => !v)) {
                                    return undefined;
                                  }
                                  return newFilter;
                                });
                              } }></FiltersCheckbox> } label={ capability.name }
                          />;
                        }) }
                      </AccordionDetails>
                    </KnowledgeAccordion> : <></> }
                  </Stack>
                </AccordionDetails>
              </FiltersAccordion> : <></> }
              { (areLevelsFetched && !levelsError && svcLevels?.length) ? <FiltersAccordion>
                <AccordionSummary
                  className="accordionSummary"
                  sx={ {
                    fontSize: "16px",
                    lineHeight: "21px",
                    fontWeight: "bold"
                  } } expandIcon={
                    <ChevronDown className="chevron" size={ 16 } /> }>
                  <span>{ t("filters_level") }</span>
                </AccordionSummary>
                <AccordionDetails className="accordionDetail">
                  <Stack sx={ { width: "100%" } }>
                    <FormControlLabel
                      sx={ {
                        marginLeft: "-9px", marginRight: "0",
                        ".MuiFormControlLabel-label": {
                          fontSize: "0.875rem"
                        }
                      } }
                      control={ <FiltersCheckbox checked={ !levelsFilter } onChange={ (evt) => {
                        if (evt.target.checked) {
                          setLevelsFilter(undefined);
                        }
                      } }></FiltersCheckbox> } label={ t("filters_all") }
                    />
                    { svcLevels?.map((level) => {
                      return <FormControlLabel
                        sx={ {
                          marginLeft: "-9px", marginRight: "0",
                          ".MuiFormControlLabel-label": {
                            fontSize: "0.875rem"
                          }
                        } }
                        key={ level.id }
                        control={ <FiltersCheckbox checked={ !!(levelsFilter?.[level.id]) } onChange={ (evt) => {
                          setLevelsFilter((old) => {
                            const newFilter = { ...old, [level.id]: evt.target.checked };
                            if (Object.values(newFilter).every(v => !v)) {
                              return undefined;
                            }
                            return newFilter;
                          });
                        } }></FiltersCheckbox> } label={ level.name }
                      />;
                    }) }
                  </Stack>
                </AccordionDetails>
              </FiltersAccordion> : <></> }
              { (campaignsEnabled && campaignsFetched && !campaignsError && svcCampaigns?.output.length) ?
                <FiltersAccordion>
                  <AccordionSummary
                    className="accordionSummary"
                    sx={ {
                      fontSize: "16px",
                      lineHeight: "21px",
                      fontWeight: "bold"
                    } } expandIcon={
                      <ChevronDown className="chevron" size={ 16 } /> }>
                    <span>{ t("filters_campaigns") }</span>
                  </AccordionSummary>
                  <AccordionDetails className="accordionDetail">
                    <Stack sx={ { width: "100%" } }>
                      <FormControlLabel
                        sx={ {
                          marginLeft: "-9px", marginRight: "0",
                          ".MuiFormControlLabel-label": {
                            fontSize: "0.875rem"
                          }
                        } }
                        control={ <FiltersCheckbox checked={ !campaignsFilter } onChange={ (evt) => {
                          if (evt.target.checked) {
                            setCampaignsFilter(undefined);
                          }
                        } }></FiltersCheckbox> } label={ t("filters_all") }
                      />
                      { svcCampaigns?.output?.map((campaign) => {
                        return <FormControlLabel
                          sx={ {
                            marginLeft: "-9px", marginRight: "0",
                            ".MuiFormControlLabel-label": {
                              fontSize: "0.875rem"
                            }
                          } }
                          key={ campaign.id }
                          control={ <FiltersCheckbox checked={ !!(campaignsFilter?.[campaign.id]) } onChange={ (evt) => {
                            setCampaignsFilter((old) => {
                              const newFilter = { ...old, [campaign.id]: evt.target.checked };
                              if (Object.values(newFilter).every(v => !v)) {
                                return undefined;
                              }
                              return newFilter;
                            });
                          } }></FiltersCheckbox> } label={ campaign.name }
                        />;
                      }) }
                    </Stack>
                  </AccordionDetails>
                </FiltersAccordion> : <></> }
              <FiltersButtonBar>
                <ButtonDigited text={ t("filters_cancel") } typology="secondary" sx={ {
                  [theme.breakpoints.down("sm")]: {
                    flexGrow: 1
                  }
                } } onClick={ () => {
                  setTypeFilter(undefined);
                  setStatusFilter(undefined);
                  setKnowledgeAreaFilter(undefined);
                  setKnowledgeDomainFilter(undefined);
                  setCapabilitiesFilter(undefined);
                  setCapabilitiesSearch("");
                  setKnowledgeSearch("");
                  setCampaignsFilter(undefined);
                  setLevelsFilter(undefined);
                  onChange?.(new URLSearchParams());
                  setOpen(false);
                  sessionStorage.removeItem(nameKeyFilterStorage);
                } }>{ t("filters_cancel") }</ButtonDigited>
                <ButtonDigited text={ t("filters_apply") } typology="primary" buttonSize="md" sx={ {
                  [theme.breakpoints.down("sm")]: {
                    flexGrow: 1
                  }
                } } onClick={ () => {
                  applyFiltersCallback(typeFilter,
                    statusFilter, capabilitiesFilter, knowledgeAreaFilter, knowledgeDomainFilter, campaignsFilter, levelsFilter);
                } }></ButtonDigited>
              </FiltersButtonBar>
            </WidgetContainer>
          ) : null
        }
      </ContentContainer>
    </SwipeableDrawer>
  </>;
}

const WidgetContainer = styled(Paper)(({ theme }) => ({
  //background: theme.customColors.drawerWidgetBg,
  background: "transparent",
  boxShadow: "none",
  height: "100vh",
  width: "400px",
  overflowY: "auto",
  padding: "20px",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2.5),

  [theme.breakpoints.down("sm")]: {
    gap: theme.spacing(3),
    maxWidth: "100%",
    minWidth: "100%",
    width: "100vw !important"
  }
}));

const KnowledgeAccordion = styled(Accordion)(({
  theme
}) => ({
  // backgroundColor:alpha(theme.customColors.backgroundSecondary, 0.10),
  background: "transparent",
  borderBottomColor: theme.palette.divider,
  borderBottomWidth: "1px",
  borderBottomStyle: "solid",
  borderRadius: "0px !important",
  boxShadow: "none",
  color: theme.customColors.textWidget,
  // marginBottom:theme.spacing(2),


  "&:before": {
    content: "none"
  },


  ".MuiAccordionSummary-root.MuiButtonBase-root": {
    minHeight: "unset",
    padding: theme.spacing(2, 0),

    ".MuiAccordionSummary-expandIconWrapper": {
      transition: "all .3s linear"
    },

    "& .MuiAccordionSummary-content ": { margin: 0 }
  },

  ".MuiAccordionDetails-root": {
    columnGap: theme.spacing(3.25),
    display: "flex",
    flexWrap: "wrap",
    maxHeight: "400px",
    rowGap: theme.spacing(2.5)


  },

  [`${theme.breakpoints.down("sm")}`]: {
    ".MuiAccordionSummary-root": {
      padding: theme.spacing(2, 1),


      "& .MuiAccordionSummary-content ": {
        "p": {
          fontSize: theme.spacing(2),
          lineHeight: "21px"
        }
      }
    }
  }

}));

const FiltersAccordion = styled(Accordion)(({
  theme
}) => ({
  // backgroundColor:alpha(theme.customColors.backgroundSecondary, 0.10),
  background: "transparent",
  borderColor: theme.palette.divider,
  borderWidth: "1px",
  borderStyle: "solid",
  borderRadius: "12px !important",
  boxShadow: "none",
  color: theme.customColors.textWidget,
  margin: "0 !important",


  "&:before": {
    content: "none"
  },


  ".MuiAccordionSummary-root": {
    minHeight: "unset",
    padding: theme.spacing(2),

    ".MuiAccordionSummary-expandIconWrapper": {
      transition: "all .3s linear"
    },

    "& .MuiAccordionSummary-content ": { margin: 0 }
  },

  ".MuiAccordionDetails-root": {
    columnGap: theme.spacing(3.25),
    display: "flex",
    flexWrap: "wrap",
    maxHeight: "400px",
    rowGap: theme.spacing(2.5)


  },

  [`${theme.breakpoints.down("sm")}`]: {
    ".MuiAccordionSummary-root": {
      // padding:theme.spacing(2,1),


      "& .MuiAccordionSummary-content ": {
        "p": {
          fontSize: theme.spacing(2),
          lineHeight: "21px"
        }
      }
    }
  }

}));

const ContentContainer = styled(Paper)((/*{theme}*/) => ({
  //background: theme.customColors.drawerWidgetBg,
  background: "transparent",
  borderRadius: "0px",
  boxShadow: "none",
  height: "auto",
  width: "100%"
}));

const StyledCloseDrawerBox = styled(Box)<
  BoxProps & { accent?: string }
>(({ accent, theme }) => ({
  alignItems: "center",
  backgroundColor: accent || theme.customColors.backgroundPrimaryCta,
  borderRadius: "0.25rem 0.25rem 0.25rem 0.25rem",
  color: theme.customColors.textPrimaryCta,
  display: "flex",
  height: "2.5rem",
  justifyContent: "center",
  opacity: 0,
  width: "2.5rem",
  [theme.breakpoints.down("sm")]: {
    borderRadius: "0.25rem 0.25rem 0.25rem 0.25rem",
    height: "2.5rem",
    width: "2.5rem"
  },

  "&.visible": {
    opacity: 1,
    transition: "opacity .3s linear"
  }
}));

export const StyledCloseDrawerButton = styled(Button)<
  ButtonProps & { $top?: string }>(({ theme, $top }) => ({
    borderRadius: "0.25rem 0 0 0.25rem",
    right: "-3.5rem",
    minWidth: "2.5rem",
    padding: "0rem",
    position: "absolute",
    top: $top || "1rem",
    width: "2.5rem",
    zIndex: "1200",

    [theme.breakpoints.down("sm")]: {
      display: "none"
    }

  }));

const CloseDrawerButton = ({
  accent,
  top,
  onClick,
  theme,
  ...props
}: Omit<BoxProps, "children">
  & Pick<ButtonProps, "onClick">
  & { accent?: string, top?: string, theme: Theme }
) => (
  <StyledCloseDrawerButton onClick={ onClick } $top={ top }>
    <StyledCloseDrawerBox accent={ accent } { ...props }>
      <LucideX size={ 24 } color={ theme.customColors.textPrimary } />
    </StyledCloseDrawerBox>
  </StyledCloseDrawerButton>
);

const SearchIcon = styled(Icon)(() => ({}));

const SearchIconWrapper = styled(Grid)(({ theme }) => ({
  alignItems: "center",
  cursor: "pointer",
  display: "flex",
  fontSize: searchBarIconSize,
  height: "100%",
  padding: theme.spacing(0, 2),
  position: "absolute",
  zIndex: 1,
  [theme.breakpoints.down("sm")]: {
    // padding: "0",
    // position: "unset"
  },

  [`${SearchIcon}`]: {
    "path": {
      fill: theme.palette.primary?.main
    }
  }
}));

const searchBarIconSize = "1.75rem";

const CancelIcon = styled(Icon)(() => ({}));

const CancelIconWrapper = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  fontSize: searchBarIconSize,
  height: "100%",
  padding: theme.spacing(0, 2),
  position: "absolute",
  right: 0,
  top: 0,

  [`${CancelIcon}`]: {
    "path": {
      fill: theme.palette.primary?.main
    }
  }
}));

const SearchInputBase = styled(InputBase)(({ theme }) => ({
  "& .MuiInputBase-input": {
    color: theme.customColors.textInput,
    padding: theme.spacing(1.25, 1, 1.25, 0),
    paddingLeft: `calc(${searchBarIconSize} + ${theme.spacing(3)})`,
    paddingRight: `calc(${searchBarIconSize} + ${theme.spacing(3)})`,
    [theme.breakpoints.down("sm")]: {
      // lineClamp: 1,
      // marginLeft: "4px",
      // padding: "0",
      // textOverflow: "ellipsis"
      // width: "80%"

    },


    "&::placeholder ": {
      color: theme.customColors.backgroundDisabled,
      fontFamily: "Inter",
      fontWeight: 400,
      opacity: 1,
      [theme.breakpoints.down("sm")]: {
        // fontSize: "14px",
        // lineHeight:"18px"


      }

    }
  },
  width: "100%"
}));

const FiltersCheckbox = styled(Checkbox)(({ theme }) => ({
  color: theme.customColors.textPrimary,
  "&.Mui-focusVisible": { boxShadow: "none" },
  boxShadow: "none",
  "input": {
    boxShadow: "none"
  },

  "&:active": {
    backgroundColor: "transparent",
    boxShadow: "none"
  },
  "&:hover": {
    backgroundColor: "transparent",
    boxShadow: "none"
  },

  "&.MuiCheckbox-root": {
    "&:hover": {
      boxShadow: "none!important",
      "input": {
        boxShadow: "none!important"
      },
      "svg": {
        boxShadow: "none!important"
      }

    }
  },
  "&.MuiCheckbox-root.Mui-checked": {
    "& svg": {
      boxShadow: "none!important",
      background: theme.customColors.backgroundPrimaryCta,
      "& path": {
        fill: theme.customColors.textPrimaryCta
      }
    },
    "&.Mui-focusVisible": {
      "input": {
        boxShadow: "none!important"
      }
    },
    "&:active": {
      "input": {
        boxShadow: "none!important"
      },
      "svg": {
        boxShadow: "none!important"
      }
    },
    "&:hover": {
      "input": {
        boxShadow: "none!important"
      },
      "svg": {
        boxShadow: "none!important"
      }
    },
    "svg": {
      "&.Mui-focusVisible": { boxShadow: "none" },
      "&:active": { boxShadow: "none" },
      "&:hover": { boxShadow: "none" },
      boxShadow: "none"
    }
  }
})
);
